import {
  FieldResponse,
  FieldValueResponse,
  FieldValueWithVersionsResponse,
  NullableString,
} from 'mapistry-shared';
import React from 'react';
import { EditFieldValueFactory } from '../../fieldDataType/values/EditFieldValueFactory';
import { ModalFormInstructions } from '../../modals/contents/ModalFormInstructions';
import { StyledFormGroup } from '../../styled/form';
import { ActiveHistoricalValueCard } from './ActiveHistoricalValueCard';

type EditFormWithoutVersionedFields = {
  fieldNamePrefix?: string;
  isSubtextHidden?: boolean;
  forceShowErrors?: boolean;
  onVersionedFieldSelected?: never;
};

// EditEntryForm doesn't support some props for versioned fields. If you need to render multiple entries
// in one form (therefor you need to provide prefix for each entry, or want to hide subtext), you need to
// re-think how it should show versioned fields.
type EditFormWithVersionedFields = {
  fieldNamePrefix?: never;
  isSubtextHidden?: never;
  forceShowErrors?: never;
  onVersionedFieldSelected?: (field: FieldResponse) => void;
};

type EditEntryFormProps = {
  children: React.ReactNode;
  entryTypeDisplayName: string;
  fields: FieldResponse[];
  fieldValues?: Record<
    FieldResponse['id'],
    FieldValueResponse | FieldValueWithVersionsResponse
  >;
  instructions?: NullableString;
  shouldHideInstructions?: boolean;
  onVersionedFieldSelected?: (field: FieldResponse) => void;
  organizationId: string;
  projectId: string | undefined;
  fieldNamePrefix?: string;
  isReadOnly?: boolean;
} & (EditFormWithoutVersionedFields | EditFormWithVersionedFields);

function FormInstructions({
  entryTypeDisplayName,
  instructions,
}: {
  entryTypeDisplayName: string;
  instructions?: NullableString;
}) {
  // not using a default argument to show the default for `null`, too
  const defaultInstructions = `Enter the required values for your ${entryTypeDisplayName}.`;
  const instructionsToShow = instructions || defaultInstructions;

  return <ModalFormInstructions>{instructionsToShow}</ModalFormInstructions>;
}

export function EditEntryForm({
  children,
  entryTypeDisplayName,
  fields,
  fieldValues,
  instructions,
  shouldHideInstructions,
  onVersionedFieldSelected = () => {
    throw new Error(
      'onVersionedFieldSelected is not provided for an entry that has historical values',
    );
  },
  organizationId,
  projectId,
  fieldNamePrefix,
  isReadOnly,
  isSubtextHidden,
  forceShowErrors,
}: EditEntryFormProps) {
  return (
    <>
      {!shouldHideInstructions && (
        <FormInstructions
          entryTypeDisplayName={entryTypeDisplayName}
          instructions={instructions}
        />
      )}
      {children}
      {fields.map((field) => {
        const fieldValue = fieldValues ? fieldValues[field.id] : undefined;
        const nameOverride = fieldNamePrefix
          ? `${fieldNamePrefix}.${field.id}`
          : undefined;
        if (field.isVersioned) {
          return (
            <ActiveHistoricalValueCard
              key={field.id}
              field={field}
              fieldValue={fieldValue}
              isReadOnly={isReadOnly}
              onVersionedFieldSelected={onVersionedFieldSelected}
              organizationId={organizationId}
              projectId={projectId}
            />
          );
        }
        return (
          <StyledFormGroup row key={field.id}>
            <EditFieldValueFactory
              field={field}
              fieldValue={fieldValue?.fieldValue}
              nameOverride={nameOverride}
              organizationId={organizationId}
              projectId={projectId}
              isReadOnly={isReadOnly}
              isSubtextHidden={isSubtextHidden}
              forceShowErrors={forceShowErrors}
            />
          </StyledFormGroup>
        );
      })}
    </>
  );
}
